import { useReducer } from 'react';
import { useLocation } from 'react-router';
import { match } from 'ts-pattern';
import { reducerWithPersistentWrapper } from '../../../../context/hooks';
import { GlobalDisplayState } from '../../../../context/types';
import { MetricIdType, Segmentation } from '../../../../types';
import { SortTypes } from '../../../types';
import { ChartTypeConfig } from '../../dashboards/types';
import { GlobalDisplayActionWithGlobalDisplayState } from '../types';
import {
  DisplayState,
  DisplayStateWithPersistence,
  LocalTimePeriodDisplayAction,
  SegmentationsToToolsSync,
  SortOrderTimePeriodDisplayAction,
  TimePeriodDisplayHandle,
} from './types';

// Stacked Bar chart (e.g. multiple metrics or 2 levels of segmentation)
const isStackedBarChart = (state: DisplayState) => {
  return match(state.chartTypeConfig)
    .with({ chartType: 'bar' }, () => state.metrics.length > 1 || state.segmentations?.length === 2)
    .otherwise(() => false);
};

export const getInitialState = (
  segmentations: Segmentation[] | undefined,
  metrics: MetricIdType[],
  chartTypeConfig: ChartTypeConfig,
  displayGlobalState: GlobalDisplayState
): DisplayState => {
  const {
    enablePercentage: enablePercentageGlobal,
    showPercentage: showPercentageGlobal,
    enableStack: enableStackGlobal,
    showStack: showStackGlobal,
    enableLabels: enableLabelsGlobal,
    showLabels: showLabelsGlobal,
    enableLegend: enableLegendGlobal,
    showLegend: showLegendGlobal,
  } = displayGlobalState;
  const initialState: DisplayState = {
    enableLabels: enableLabelsGlobal,
    showLabels: showLabelsGlobal,
    enableLegend: enableLegendGlobal,
    showLegend: showLegendGlobal,
    showTableView: false,
    enablePercentage: enablePercentageGlobal,
    showPercentage: showPercentageGlobal,
    enableStack: enableStackGlobal,
    showStack: showStackGlobal,
    enableSortOrder: true,
    selectedSortOrder: SortTypes.ASC,
    segmentations,
    metrics,
    chartTypeConfig,
    globalDisplayState: displayGlobalState,
  };
  return initialState;
};

export const useDisplayWithPersistence = (
  chartId: string,
  chartTypeConfig: ChartTypeConfig,
  segmentations: Segmentation[] | undefined,
  metrics: MetricIdType[],
  displayGlobalState: GlobalDisplayState
): TimePeriodDisplayHandle => {
  const initialState = getInitialState(segmentations, metrics, chartTypeConfig, displayGlobalState);
  const location = useLocation();
  const persistenceKey = `${location.pathname}-${chartId}-display`;
  const stored: string | null = localStorage.getItem(persistenceKey);
  const storedItems: DisplayStateWithPersistence | null = stored
    ? (JSON.parse(stored) as unknown as DisplayStateWithPersistence)
    : null;
  const state = storedItems ?? { ...initialState, persistenceKey };
  return useReducer(reducerWithPersistentWrapper(reducer), state);
};

export const reducer = (
  state: DisplayState,
  action:
    | GlobalDisplayActionWithGlobalDisplayState
    | LocalTimePeriodDisplayAction
    | SortOrderTimePeriodDisplayAction
    | SegmentationsToToolsSync
): DisplayState => {
  const stackedBarChart = isStackedBarChart(state);
  const newState = match(action)
    .with({ type: 'segmentations-to-tools-sync' }, (a: SegmentationsToToolsSync) => {
      const stackedBarChart = isStackedBarChart({ ...state, segmentations: a.segmentations });
      const enablePercentage = stackedBarChart && !state.showTableView && state.globalDisplayState.enablePercentage;
      const enableStack =
        stackedBarChart && !state.showTableView && !state.showPercentage && state.globalDisplayState.enableStack;
      const showStack = enableStack && state.showStack;

      return {
        ...state,
        enablePercentage,
        enableStack,
        showStack,
        segmentations: a.segmentations,
      };
    })
    .with({ type: 'toggle-labels' }, () => {
      return {
        ...state,
        showLabels: !state.showLabels,
      };
    })
    .with({ type: 'toggle-percentage' }, () => {
      const showPercentage = stackedBarChart && !state.showPercentage;
      return {
        ...state,
        showPercentage,
        enableStack: !showPercentage,
      };
    })
    .with({ type: 'toggle-stack' }, () => {
      const showStack = stackedBarChart && !state.showStack;
      return {
        ...state,
        showStack,
        enablePercentage: !showStack,
      };
    })
    .with({ type: 'toggle-legend' }, () => {
      return {
        ...state,
        showLegend: !state.showLegend,
      };
    })
    .with({ type: 'sort-order' }, (a: SortOrderTimePeriodDisplayAction) => {
      return {
        ...state,
        selectedSortOrder: a.selectedSortOrder,
      };
    })
    .with({ type: 'toggle-tableView' }, () => {
      const enablePercentage = stackedBarChart && state.showTableView && state.globalDisplayState.enablePercentage;
      const enableStack =
        stackedBarChart && state.showTableView && !state.showPercentage && state.globalDisplayState.enableStack;

      const enableLabels = state.showTableView;
      const enableLegend = state.showTableView;
      const enableSortOrder = state.showTableView;
      return {
        ...state,
        showTableView: !state.showTableView,
        enableSortOrder,
        enablePercentage,
        enableStack,
        enableLabels,
        enableLegend,
      };
    })
    .with({ type: 'global-state-sync' }, (a) => {
      const getDisplayStateValue = (localStateValue: boolean, globalDisplayKey: keyof GlobalDisplayState) => {
        const shouldChange = state.globalDisplayState[globalDisplayKey] !== a.globalDisplayState[globalDisplayKey];
        return shouldChange ? a.globalDisplayState[globalDisplayKey] : localStateValue;
      };

      const showLabels = getDisplayStateValue(state.showLabels, 'showLabels');
      const showPercentage = getDisplayStateValue(state.showPercentage, 'showPercentage');
      const enablePercentage = getDisplayStateValue(state.enablePercentage, 'enablePercentage');
      const showStack = getDisplayStateValue(state.showStack, 'showStack');
      const enableStack = getDisplayStateValue(state.enableStack, 'enableStack');
      const showLegend = getDisplayStateValue(state.showLegend, 'showLegend');

      const finalShowPercentage = stackedBarChart && showPercentage;
      const finalShowStack = stackedBarChart && !showPercentage && showStack;
      const finalEnablePercentage = stackedBarChart && !state.showTableView && enablePercentage;
      const finalEnableStack = stackedBarChart && !showPercentage && enableStack;

      return {
        ...state,
        showLabels,
        showPercentage: finalShowPercentage,
        showStack: finalShowStack,
        showLegend,
        enablePercentage: finalEnablePercentage,
        enableStack: finalEnableStack,
        globalDisplayState: a.globalDisplayState,
      };
    })
    .exhaustive();
  return { ...newState };
};
